import React, { useState } from 'react';
import TextEditor from '@components/form-elements/TextEditor';
import { fetchJson } from '@utils/fetchJson';
import CommentItem from './CommentItem';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL } from '@config';
import WSLoader from '@uiElements/WSLoader';

const CommentsList = ({ record_id, record_type = 'task' }) => {
  const queryClient = useQueryClient();
  const [sortOrder, setSortOrder] = useState('DESC');
  const [editorContent, setEditorContent] = useState('');
  const [showEditor, setShowEditor] = useState(false);

  // -------------------------------
  // Fetch Comments (Infinite Query)
  // -------------------------------
  const fetchCommentsApi = async ({ pageParam = 0 }) => {
    const params = {
      status: 'active',
      orderBy: 'created_date',
      order: sortOrder,
      record_id,
      type: record_type,
      curpage: pageParam,
    };

    const res = await fetchJson(`${API_BASE_URL}/comments`, {
      method: 'POST',
      body: JSON.stringify(params),
    });

    return {
      data: res.data || [],
      nextPage: res.paginginfo?.nextpage ?? null,
      hasMore: res.loadstate ?? false,
      total: res.paginginfo?.totalRecords ?? 0,
    };
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useInfiniteQuery({
    queryKey: ['comments', record_type, record_id, sortOrder],
    queryFn: fetchCommentsApi,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextPage : undefined),
    staleTime: 1000 * 30,
  });

  const comments = data?.pages.flatMap((p) => p.data) || [];
  const totalComments = data?.pages?.[0]?.total || 0;

  // -------------------------------
  // Add Comment Mutation
  // -------------------------------
  const addCommentMutation = useMutation({
    mutationFn: async () => {
      const body = {
        body: editorContent,
        created_date: null,
        status: 'active',
        mentions: [],
        record_id,
        type: record_type,
      };

      return await fetchJson(`${API_BASE_URL}/comment/`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      setEditorContent('');
      setShowEditor(false);

      // invalidate and reload first page
      queryClient.invalidateQueries(['comments', record_type, record_id, sortOrder]);
    }
  });

  const toggleSort = () => {
    setSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
  };

  return (
    <div className="p-0">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-sm font-semibold">
          All Comments ({totalComments})
        </h2>

        <button onClick={toggleSort} className="text-sm text-blue-500">
          {sortOrder === 'ASC' ? 'Oldest First' : 'Newest First'}
        </button>
      </div>

      {/* Add Comment Box */}
      {!showEditor ? (
        <button
          onClick={() => setShowEditor(true)}
          className="text-sm w-full justify-left flex rounded p-3 border text-blue-500 mb-2"
        >
          Add a comment...
        </button>
      ) : (
        <>
          <TextEditor
            value={editorContent}
            showEditorInitially={true}
            onChange={setEditorContent}
          />

          <div className="mt-2 flex gap-2">
            <button
              onClick={() => addCommentMutation.mutate()}
              disabled={!editorContent.trim() || addCommentMutation.isLoading}
              className="bg-blue-600 text-white px-4 py-1 rounded text-sm"
            >
              Post Comment
            </button>

            <button
              onClick={() => {
                setEditorContent('');
                setShowEditor(false);
              }}
              className="text-sm text-gray-500 hover:underline"
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {/* Comments List */}
      <div className="mt-6 space-y-4">
        {isLoading && <WSLoader></WSLoader>}

        {!isLoading && comments.length === 0 && <div>No comments yet</div>}

        {comments.map((item) => (
          <CommentItem
            key={item.comment_id}
            item={item}
            record_type={record_type}
            onUpdated={() =>
              queryClient.invalidateQueries(['comments', record_type, record_id, sortOrder])
            }
          />
        ))}

        {/* Load More */}
        {hasNextPage && (
          <button
            className="text-sm text-blue-500 mt-4"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Loading...' : 'Load More'}
          </button>
        )}
      </div>
    </div>
  );
};

export default CommentsList;
